import { useEffect, useState } from 'react';
import { captureError } from './captureError';
import { track } from '../tracking';
import moment from 'moment';
import getDicomRecords, { DicomDocument } from '../api/getDicomRecords';
import deleteDicomRecordApi from '../api/deleteDicomRecord';
import LinkDicomModal from './LinkDicomModal';
import { downArrowIcon } from '../components/common/Icons';
import { Dropdown, Menu, notification } from 'antd';
import { OptionItem } from './Appointments';

const downArrow = downArrowIcon();

interface Props {
    uuid: string;
    p_oid: string;
}

const DicomContainer: React.FC<Props> = ({ uuid, p_oid }) => {
    const [fetchingDicomDocuments, setFetchingDicomDocuments] = useState<boolean>(false);
    const [dicomDocuments, setDicomDocuments] = useState<DicomDocument[]>([]);
    const [isLinkDicomModalOpen, setIsLinkDicomModalOpen] = useState<boolean>(false);
    const [nextToken, setNextToken] = useState<string>('');

    useEffect(() => {
        fetchDicomRecords();
    }, []);

    async function fetchDicomRecords(nextToken?: string) {
        if (!nextToken) {
            setFetchingDicomDocuments(true);
            setDicomDocuments([]);
        }
        try {
            const { dicoms, next_token } = await getDicomRecords(uuid, nextToken);
            if (Array.isArray(dicoms)) {
                if (nextToken) {
                    setDicomDocuments([...dicomDocuments, ...dicoms]);
                } else {
                    setDicomDocuments(dicoms);
                }
            } else {
                captureError(new Error('dicoms is not an array'));
                track('ERROR', {
                    on: 'fetchDicomRecords',
                    error: 'dicoms is not an array',
                });
            }
            setNextToken(next_token);
        } catch (error) {
            captureError(error as Error);
            track('ERROR', {
                on: 'fetchDicomRecords',
                error: (error as Error).message,
            });
        } finally {
            setFetchingDicomDocuments(false);
        }
    }

    const refreshDicomRecords = () => {
        setNextToken('');
        fetchDicomRecords();
    };

    const toggleLinkDicomModal = () => {
        setIsLinkDicomModalOpen(!isLinkDicomModalOpen);
    };

    const aggregatedByMonth = dicomDocuments.reduce((acc, curr) => {
        const date = moment(curr.created_at * 1000).format('YYYY-MM');
        if (!acc[date]) {
            acc[date] = [];
        }
        acc[date].push(curr);
        return acc;
    }, {} as { [key: string]: DicomDocument[] });

    const deleteDicomRecord = async (key: string) => {
        try {
            await deleteDicomRecordApi(key);
            notification.success({
                message: 'Dicom record deleted successfully',
                duration: 5,
            });
            track('DICOM_RECORD_DELETED', { key });
        } catch (error) {
            captureError(error as Error);
            track('ERROR', {
                on: 'deleteDicomRecord',
                error: (error as Error).message,
            });
        } finally {
            refreshDicomRecords();
        }
    };

    if (fetchingDicomDocuments) {
        return (
            <div className="h-full w-full flex items-center justify-center">
                <svg
                    className="w-24 h-24 fill-actionPrimary animate-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 512 512"
                >
                    <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                </svg>
            </div>
        );
    }

    return (
        <div className="h-full w-full flex flex-col px-24 py-16 space-y-24 overflow-hidden">
            <div className="flex justify-end">
                <button
                    className="primary-btn-v3 btn--small px-12 py-8"
                    onClick={toggleLinkDicomModal}
                >
                    Link DICOM
                </button>
            </div>

            {dicomDocuments.length ? (
                <div className="flex-1 overflow-auto">
                    {Object.keys(aggregatedByMonth)
                        .sort((a, b) => {
                            return moment(a).isAfter(moment(b)) ? -1 : 1;
                        })
                        .map((groupDate) => (
                            <div>
                                <p className="caption-1 bold text-darwin-neutral-1000 mb-12">
                                    {moment(groupDate).format('MMM YYYY')}
                                </p>
                                <div className="grid grid-cols-3 gap-16">
                                    {aggregatedByMonth[groupDate].map((dicomRecord) => {
                                        return (
                                            <div
                                                key={dicomRecord.key}
                                                className="rounded-8 bg-white p-16"
                                                style={{ boxShadow: '0px 1px 2px #c6c6c6' }}
                                            >
                                                <div
                                                    className="cursor-pointer"
                                                    onClick={() => {
                                                        window.open(dicomRecord.loc, '_blank');
                                                        track('DICOM_RECORD_VIEWED', {
                                                            loc: dicomRecord.loc,
                                                            key: dicomRecord.key,
                                                        });
                                                    }}
                                                >
                                                    <p className="text-11 text-darwin-neutral_new-800">
                                                        {dicomRecord.study_date
                                                            ? moment(dicomRecord.study_date).format(
                                                                  "DD MMM'YY",
                                                              )
                                                            : '-'}
                                                    </p>
                                                    <div className="flex items-center justify-center py-16 w-full">
                                                        <img
                                                            src="/images/dicom-file.png"
                                                            style={{ objectFit: 'contain' }}
                                                            className="max-h-140"
                                                        />
                                                    </div>
                                                </div>
                                                <div
                                                    className="flex items-center justify-between"
                                                    id={dicomRecord.key}
                                                >
                                                    <div>
                                                        {dicomRecord.patient_data?.first_name}{' '}
                                                        {dicomRecord.patient_data?.last_name}
                                                    </div>
                                                    <Dropdown
                                                        overlay={
                                                            <Menu>
                                                                <OptionItem
                                                                    label={'Delete Record'}
                                                                    onClick={() => {
                                                                        deleteDicomRecord(
                                                                            dicomRecord.key,
                                                                        );
                                                                    }}
                                                                />
                                                            </Menu>
                                                        }
                                                        placement="bottomRight"
                                                        trigger={['hover']}
                                                        arrow={true}
                                                    >
                                                        <svg
                                                            viewBox="0 0 28 28"
                                                            className="w-28 h-28 fill-neutral-5 cursor-pointer"
                                                            preserveAspectRatio="xMidYMid meet"
                                                        >
                                                            <path d="M12,16A2,2 0 0,1 14,18A2,2 0 0,1 12,20A2,2 0 0,1 10,18A2,2 0 0,1 12,16M12,10A2,2 0 0,1 14,12A2,2 0 0,1 12,14A2,2 0 0,1 10,12A2,2 0 0,1 12,10M12,4A2,2 0 0,1 14,6A2,2 0 0,1 12,8A2,2 0 0,1 10,6A2,2 0 0,1 12,4Z"></path>
                                                        </svg>
                                                    </Dropdown>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    {nextToken && (
                        <div
                            className="flex justify-center select-none mt-16"
                            onClick={() => fetchDicomRecords(nextToken)}
                        >
                            <button className="flex items-center bg-white border border-darwin-primary-500 text-16 font-700 text-darwin-primary-500 px-16 py-5 rounded-4 space-x-8">
                                {downArrow}
                                <span>Show more</span>
                            </button>
                        </div>
                    )}
                </div>
            ) : (
                <div className="flex-1 overflow-auto flex flex-col items-center justify-center space-y-12">
                    <img src="/images/dicom-file.png" className="w-80 h-80" />
                    <p className="text-neutral-3">No dicom records found for this patient</p>
                </div>
            )}

            <LinkDicomModal
                isVisible={isLinkDicomModalOpen}
                toggleLinkDicomModal={toggleLinkDicomModal}
                p_oid={p_oid}
                refreshDicomRecords={refreshDicomRecords}
            />
        </div>
    );
};

export default DicomContainer;
